import { company_name } from "@/i18n/variables";

const sign_in = {
	/* -------------------------------------------------------------------------- */
	/*                            ACTIVATE ACCOUNT PAGE                           */
	/* -------------------------------------------------------------------------- */
	activate: {
		description: "Complete the registration of your account by setting a password.",
		status: {
			expired: {
				description:
					"The activation link has expired and cannot be used. Please request a new invitation from your account administrator.",
				title: "Activation Link Expired!",
			},
			invalid: {
				description:
					"Your request to activate your account has been declined. Check your link or contact us for persistent issues.",
				title: "Account Not Activated!",
			},
			success: {
				description: "Your account is active and you can log in.",
				title: "Your final username has become <strong>{{username}}</strong>!",
			},
			unknown: {
				description:
					"There was an issue adding a new user; please try again or contact our support department for persistent issues.",
				title: "Account Not Activated!",
			},
		},
		title: "Complete Registration",
	},

	/* -------------------------------------------------------------------------- */
	/*                           DEBLOCK IP-ADDRESS PAGE                          */
	/* -------------------------------------------------------------------------- */
	deblock_ip: {
		status: {
			expired: {
				description:
					"Your request has expired, and the link is no longer valid. You can request password reset again.",
				title: "Request Expired!",
			},
			invalid: {
				description: "The unlock link you entered is invalid and is not recognized by us.",
				title: "Invalid Unlock Link!",
			},
			success: {
				description:
					"Your IP address has been successfully unlocked, and you can now log in from your IP address.",
				title: "IP Address Successfully Unlocked!",
			},
			unknown: {
				description:
					"There was an issue, and we cannot unblock your IP address. Please contact support.",
				title: "Failed to Unblock IP Address!",
			},
		},
		title: "Unlock IP Address",
	},

	/* -------------------------------------------------------------------------- */
	/*                           FORGOT PASSWORD SECTION                          */
	/* -------------------------------------------------------------------------- */
	forgot_password: {
		confirmation: {
			description:
				"If your username is known to us, you will receive an email with a link to set a new password. Haven't received an email? Check your spam filter.",
			title: "Email Sent",
		},
		description:
			"Enter your username, and you will receive an email with a link to reset your password.",
		disabled_user: {
			description:
				"If your username is known to us, you will receive an email with a link to set a new password. If you haven't received an email, your username may not be known to us.",
		},
		hostfact_user: {
			description:
				"You are trying to log in with a klanten.tuxis.nl account. Perhaps a personal account has been created. Log in with that account. If this is the first time you are logging into this Portal with your klanten.tuxis.nl credentials, please recover your password at https://klanten.tuxis.nl and then return here.",
		},
		title: "Forgot Password?",
	},

	/* -------------------------------------------------------------------------- */
	/*                          FORGOT USERNAME SECTION                          */
	/* -------------------------------------------------------------------------- */
	forgot_username: {
		confirmation: {
			description:
				"If your email address is known to us, you will receive your username by email. Haven't received an email? Check your spam filter.",
			title: "Email Sent",
		},
		description:
			"Enter the email address you use on this portal, and you will receive a list of all registered usernames associated with this email address.",
		title: "Forgot Username?",
	},
	/* -------------------------------------------------------------------------- */
	/*                                SIGN IN PAGE                                */
	/* -------------------------------------------------------------------------- */
	index: {
		confirm_email: {
			button: "Go to Homepage",
			message:
				"A confirmation email has been sent to <strong>{{email_address}}</strong> for address validation. This email contains instructions to validate your email address and all the confirmation details you need. It is important that you complete the validation to ensure we can process your request.",
			title: "Confirm Email Address",
		},
		form: {
			description: "To access the portal, please log in with your username and password.",
			links: {
				forgot_password: "Forgot Password?",
				forgot_username: "Forgot Username?",
				new_user: {
					part1: `Not a ${company_name} customer yet?`,
					part2: "Create an account here!",
				},
			},
			register: {
				title: "Create an account",
			},
			title: "Sign In",
		},
		modal: {
			description:
				"2FA is enabled on your account. Please enter your authorization code to access the portal.",
			title: "Two-Factor Authentication (2FA)",
		},
		section: {
			description: {
				href: "https://support.tuxis.nl/en/knowledgebase/article/using-your-klanten-tuxis-nl-account-and-transferring-it-to-portal-tuxis-cloud-portal",
				text: "You can order and manage products on this portal. Create a free personal account in Portal for yourself and employees and/or external parties who need to do something for you. For example, you can arrange that someone can create a VPS but cannot view invoices. You can also link products to relationships so that you always know which department or customer uses a particular product or service.<br/><br/>Existing customer and new to this portal? Read <a>here</a> what you need to do to log in to Portal for the first time. New customer? Then click on sign up.",
			},
			title: "High-End Solutions for Ambitious Businesses",
		},
		status: {
			debtor_blocked: "Debtor blocked. Please contact our support department.",
			email_resend: "Success! We have resent you a confirmation email.",
			expired_hostfact_user: {
				msg: {
					href: "https://support.tuxis.nl/en/tickets/create/step1",
					text: "You are trying to log in with your customers.tuxis account. Since a personal account has already been created, you should use it to log in. If you do not yet have a personal account, please contact the colleague who may have a personal account. Don't know who it is? Then ask via a ticket on <a>support.tuxis.nl</a>.",
				},
				title: "Use personal account",
			},
			hostfact_debtor_mapping_issue:
				"At the moment, it is not possible to log in with your existing debtor number. Please contact our support department.",
			inactive: "You must confirm your email address before gaining access to your account.",
			invalid: "Your username or password is incorrect.",
			invalid_format:
				'Error code: Username does not meet the desired format. Use "username@DBxxxx" with xxxx as the debtor number.',
			open_invite:
				"You have not activated your account yet; you can do so by clicking the link in the email you received from us. Haven't received an email? Contact your account administrator.",
			user_blocked: "User blocked. Please contact your administrator.",
			user_blocked_by_ip: {
				part_1: "Your IP address is blocked due to too many failed login attempts.",
				part_2: "Unlock your account",
			},
		},
	},

	/* -------------------------------------------------------------------------- */
	/*                             RESET PASSWORD PAGE                            */
	/* -------------------------------------------------------------------------- */
	password_reset: {
		description:
			"You can now set a new password; after setting it, your old password will be invalid.",
		error_messages: {
			no_match: "Your passwords do not match!",
		},
		status: {
			expired: {
				description:
					"Your request has expired, and the link is no longer valid. You can request password reset again.",
				title: "Request Expired!",
			},
			invalid: {
				description:
					"Your request to change your password has been declined. Check your link or contact us for persistent issues.",
				title: "Request Declined!",
			},
			not_allowed: {
				description:
					"The password of a HostFact user cannot be changed through the Portal. This must be done through HostFact.",
				title: "HostFact Password Cannot Be Changed!",
			},
			success: {
				description:
					"Your password has been successfully changed, and you can now log in with your new password.",
				title: "Password Successfully Changed",
			},
			unknown: {
				description:
					"Your request to change your password has been declined. Check your link or contact us for persistent issues.",
				title: "Request Declined!",
			},
		},
		title: "Reset Password",
	},

	/* -------------------------------------------------------------------------- */
	/*                           REQUEST DEBLOCK SECTION                          */
	/* -------------------------------------------------------------------------- */
	request_deblock_link: {
		confirmation: {
			description:
				"If your email address is known to us, you will receive an email with a link to unlock your account. Haven't received an email? Check your spam filter.",
			title: "Email Sent",
		},
		description:
			"Your IP address is blocked due to too many failed login attempts. You can unlock your account by entering your username below. You will receive an email with a link to unlock.",
		title: "Unlock IP Address",
	},
};

export default sign_in;
